import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RequestStatus } from '@/core/interfaces/common';

import { AnalyticsState } from '@/features/Analytics/interfaces';
import {
  DEFAULT_TIME_FRAME,
  DEFAULT_WIDGET_ORDER,
  VolumeOverTimeFilterValue,
  WidgetId,
} from '@/features/Analytics/constants';

import { decodeHtml, getActionPrefix } from '@/utils/helpers';

import {
  fetchCategoryCountry,
  fetchHeatmapSubcategories,
  fetchModalityRadar,
  fetchPreciseHeatmap,
  fetchTimeseries,
  fetchVolumeByCountry,
} from './actions';

const actionPrefix = getActionPrefix('analytics');

const initialState: AnalyticsState = {
  isAnalyticsDashboardOpen: false,
  isExpanded: false,
  timeFrame: DEFAULT_TIME_FRAME,
  widgetOrder: [...DEFAULT_WIDGET_ORDER],
  volumeOverTimeFilter: VolumeOverTimeFilterValue.SUB_CATEGORY,
  volumeByCountryFilter: 'crime',
  heatmapCategoryId: null,
  heatmapCategoriesWithData: {},
  volumeByCountryCategoriesWithData: {},
  volumeByCountrySelectedCategoryId: null,
  charts: {
    volumeOverTime: {
      data: null,
      status: RequestStatus.IDLE,
      error: null,
    },
    modalityDistribution: {
      data: null,
      status: RequestStatus.IDLE,
      error: null,
    },
    preciseHeatmap: {
      data: null,
      status: RequestStatus.IDLE,
      error: null,
    },
    volumeByCountry: {
      data: null,
      status: RequestStatus.IDLE,
      error: null,
    },
  },
};

export const analyticsSlice = createSlice({
  name: actionPrefix,
  initialState,
  reducers: {
    openAnalyticsDashboard: state => {
      state.isAnalyticsDashboardOpen = true;
    },
    closeAnalyticsDashboard: state => {
      state.isAnalyticsDashboardOpen = false;
    },
    setTimeFrame: (state, action: PayloadAction<AnalyticsState['timeFrame']>) => {
      state.timeFrame = action.payload;
    },
    setWidgetOrder: (state, action: PayloadAction<Array<WidgetId>>) => {
      state.widgetOrder = action.payload;
    },
    setVolumeOverTimeFilter: (state, action: PayloadAction<VolumeOverTimeFilterValue>) => {
      state.volumeOverTimeFilter = action.payload;
    },
    setVolumeByCountryFilter: (state, action: PayloadAction<string>) => {
      state.volumeByCountryFilter = action.payload;
    },
    setHeatmapCategoryId: (state, action: PayloadAction<number | null>) => {
      state.heatmapCategoryId = action.payload;
    },
    setVolumeByCountrySelectedCategoryId: (state, action: PayloadAction<number | null>) => {
      state.volumeByCountrySelectedCategoryId = action.payload;
    },
    toggleExpanded: state => {
      state.isExpanded = !state.isExpanded;
    },
    resetCategoriesWithData: state => {
      state.heatmapCategoriesWithData = {};
      state.volumeByCountryCategoriesWithData = {};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTimeseries.pending, state => {
        state.charts.volumeOverTime.status = RequestStatus.LOADING;
        state.charts.volumeOverTime.error = null;
      })
      .addCase(fetchTimeseries.fulfilled, (state, action) => {
        state.charts.volumeOverTime.status = RequestStatus.SUCCEEDED;
        state.charts.volumeOverTime.data = action.payload;
      })
      .addCase(fetchTimeseries.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.charts.volumeOverTime.status = RequestStatus.FAILED;
        state.charts.volumeOverTime.error = action.error.message ?? 'Unknown error';
      })
      .addCase(fetchModalityRadar.pending, state => {
        state.charts.modalityDistribution.status = RequestStatus.LOADING;
        state.charts.modalityDistribution.error = null;
      })
      .addCase(fetchModalityRadar.fulfilled, (state, action) => {
        state.charts.modalityDistribution.status = RequestStatus.SUCCEEDED;
        state.charts.modalityDistribution.data = action.payload;
      })
      .addCase(fetchModalityRadar.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.charts.modalityDistribution.status = RequestStatus.FAILED;
        state.charts.modalityDistribution.error = action.error.message ?? 'Unknown error';
      })
      .addCase(fetchPreciseHeatmap.pending, state => {
        state.charts.preciseHeatmap.status = RequestStatus.LOADING;
        state.charts.preciseHeatmap.error = null;
      })
      .addCase(fetchPreciseHeatmap.fulfilled, (state, action) => {
        state.charts.preciseHeatmap.status = RequestStatus.SUCCEEDED;
        state.charts.preciseHeatmap.data = action.payload;
      })
      .addCase(fetchPreciseHeatmap.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.charts.preciseHeatmap.status = RequestStatus.FAILED;
        state.charts.preciseHeatmap.error = action.error.message ?? 'Unknown error';
      })
      .addCase(fetchVolumeByCountry.pending, state => {
        state.charts.volumeByCountry.status = RequestStatus.LOADING;
        state.charts.volumeByCountry.error = null;
      })
      .addCase(fetchVolumeByCountry.fulfilled, (state, action) => {
        state.charts.volumeByCountry.status = RequestStatus.SUCCEEDED;
        state.charts.volumeByCountry.data = action.payload;
      })
      .addCase(fetchVolumeByCountry.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.charts.volumeByCountry.status = RequestStatus.FAILED;
        state.charts.volumeByCountry.error = action.error.message ?? 'Unknown error';
      })
      .addCase(fetchHeatmapSubcategories.fulfilled, (state, action) => {
        const map: { [categoryId: number]: boolean } = {};

        action.payload.subcategories.forEach(s => {
          map[s.id] = true;
        });
        state.heatmapCategoriesWithData = map;
      })
      .addCase(fetchHeatmapSubcategories.rejected, state => {
        state.heatmapCategoriesWithData = {};
      })
      .addCase(fetchCategoryCountry.fulfilled, (state, action) => {
        const map: { [categoryName: string]: boolean } = {};

        action.payload.categories.forEach(c => {
          map[decodeHtml(c)] = true;
        });
        state.volumeByCountryCategoriesWithData = map;
      })
      .addCase(fetchCategoryCountry.rejected, state => {
        state.volumeByCountryCategoriesWithData = {};
      });
  },
});

export const {
  openAnalyticsDashboard,
  closeAnalyticsDashboard,
  setTimeFrame,
  setWidgetOrder,
  setVolumeOverTimeFilter,
  setVolumeByCountryFilter,
  setHeatmapCategoryId,
  setVolumeByCountrySelectedCategoryId,
  toggleExpanded,
  resetCategoriesWithData,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
